import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Briefcase, Building2, Calendar, CheckCircle2, ExternalLink, Info, MapPin, Target, Users } from "lucide-react";
import { experiences } from "../data/mock";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";

const META_ICON_MAP = {
	Period: Calendar,
	Duration: Calendar,
	Location: MapPin,
	Team: Users,
	Type: Briefcase,
	Role: Target,
};
const metaIcon = (label) => {
	const Icon = META_ICON_MAP[label] ?? Info;
	return <Icon className="w-4 h-4" />;
};

// ---------- media helpers ----------
const getMediaType = (item) => {
	if (item && typeof item === "object" && item.type) return item.type;
	if (typeof item === "string") {
		if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(item)) return "video";
		if (/youtube\.com|youtu\.be/.test(item)) return "youtube";
	}
	return "image";
};
const getMediaSrc = (item) => (item && typeof item === "object" ? item.src : item);
const getYouTubeEmbedId = (src) => {
	if (!src) return "";
	if (src.includes("youtu.be/")) return src.split("youtu.be/")[1].split("?")[0];
	if (src.includes("v=")) return src.split("v=")[1].split("&")[0];
	return src;
};

const MediaSlide = ({ item, alt, className }) => {
	const type = getMediaType(item);
	const src = getMediaSrc(item);
	if (type === "video") {
		return (
			<video src={src} poster={item?.poster} className={className} controls muted playsInline>
				<source src={src} />
			</video>
		);
	}
	if (type === "youtube") {
		const id = getYouTubeEmbedId(src);
		return (
			<iframe
				className={className}
				src={`https://www.youtube.com/embed/${id}`}
				title={alt}
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				allowFullScreen
			/>
		);
	}
	return <img src={src} alt={alt} className={className} />;
};

const MediaCarousel = ({ items, title }) => {
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
	const [canPrev, setCanPrev] = useState(false);
	const [canNext, setCanNext] = useState(false);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setCanPrev(emblaApi.canScrollPrev());
		setCanNext(emblaApi.canScrollNext());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		emblaApi.on("select", onSelect);
		onSelect();
	}, [emblaApi, onSelect]);

	return (
		<div className="relative rounded-xl overflow-hidden border border-teal-500/15 bg-[#08201d]">
			<div ref={emblaRef} className="overflow-hidden">
				<div className="flex">
					{items.map((item, i) => (
						<div key={i} className="flex-none w-full">
							<MediaSlide item={item} alt={`${title} ${i + 1}`} className="w-full h-72 object-cover" />
						</div>
					))}
				</div>
			</div>
			{items.length > 1 && (
				<>
					<button
						onClick={() => emblaApi?.scrollPrev()}
						disabled={!canPrev}
						className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-black/70 disabled:opacity-30 transition"
					>
						<ArrowLeft className="w-4 h-4" />
					</button>
					<button
						onClick={() => emblaApi?.scrollNext()}
						disabled={!canNext}
						className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-black/70 disabled:opacity-30 transition"
					>
						<ArrowRight className="w-4 h-4" />
					</button>
				</>
			)}
		</div>
	);
};

const Block = ({ icon, title, children }) => (
	<div className="rounded-2xl border border-teal-500/15 bg-[#08201d]/60 p-7">
		<div className="flex items-center gap-2 text-teal-300 mb-5">
			{icon}
			<h2 className="font-display text-lg font-semibold">{title}</h2>
		</div>
		{children}
	</div>
);

const MetaCard = ({ icon, label, value }) => (
	<div className="flex items-start gap-3 p-4 rounded-xl bg-teal-500/5 border border-teal-500/15">
		<div className="text-teal-400 mt-0.5 shrink-0">{icon}</div>
		<div>
			<div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
			<div className="text-sm font-medium text-slate-200">{value}</div>
		</div>
	</div>
);

const ExperienceDetail = () => {
	const { slug } = useParams();
	const experience = experiences.find((e) => e.slug === slug);

	useEffect(() => { window.scrollTo(0, 0); }, [slug]);

	if (!experience) {
		return (
			<div className="min-h-screen bg-[#04100f] flex flex-col items-center justify-center text-slate-300 gap-4">
				<p className="text-xl">Experience not found.</p>
				<Link to="/" className="text-teal-400 hover:underline">← Back home</Link>
			</div>
		);
	}

	const { role, company, period, location, logo, meta = [], overview, bullets = [], results = [], galleries = [], links = [] } = experience;

	return (
		<div className="min-h-screen bg-[#04100f] text-slate-100">
			<Header />

			{/* Hero */}
			<div className="relative pt-24 pb-16 border-b border-teal-500/10 bg-gradient-to-b from-[#08201d]/80 to-transparent">
				<div className="max-w-7xl mx-auto px-6 lg:px-10">
					<Link to="/#experience" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-teal-300 transition-colors mb-8">
						<ArrowLeft className="w-4 h-4" /> Back to experience
					</Link>

					<div className="flex items-start gap-6">
						<div className="w-20 h-20 rounded-2xl border border-teal-500/20 bg-[#08201d] flex items-center justify-center shrink-0 overflow-hidden">
							{logo
								? <img src={logo} alt={company} className="w-full h-full object-cover" />
								: <Building2 className="w-9 h-9 text-teal-500/40" />
							}
						</div>
						<div>
							<div className="flex items-center gap-2 text-teal-300 mb-1">
								<Briefcase className="w-4 h-4" />
								<span className="text-sm font-mono">{company}</span>
							</div>
							<h1 className="font-display text-3xl lg:text-5xl font-bold text-slate-100 leading-tight">{role}</h1>
							<div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-400">
								<span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{period}</span>
								<span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{location}</span>
							</div>
						</div>
					</div>

					{meta.length > 0 && (
						<div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
							{meta.map((m) => (
								<MetaCard key={m.label} icon={metaIcon(m.label)} label={m.label} value={m.value} />
							))}
						</div>
					)}
				</div>
			</div>

			{/* Body */}
			<div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
				<div className="grid lg:grid-cols-12 gap-10">
					<div className="lg:col-span-8 space-y-8">

						{overview && (
							<Block icon={<Info className="w-5 h-5" />} title="Overview">
								<p className="text-slate-300 leading-relaxed">{overview}</p>
							</Block>
						)}

						{bullets.length > 0 && (
							<Block icon={<Briefcase className="w-5 h-5" />} title="Responsibilities">
								<ul className="space-y-3">
									{bullets.map((b, i) => (
										<li key={i} className="flex items-baseline gap-3 text-sm text-slate-300 leading-relaxed">
											<span className="text-teal-400 shrink-0">▪</span>
											<span>{b}</span>
										</li>
									))}
								</ul>
							</Block>
						)}

						{results.length > 0 && (
							<Block icon={<CheckCircle2 className="w-5 h-5" />} title="Results & impact">
								<ul className="space-y-3">
									{results.map((r, i) => (
										<li key={i} className="flex items-start gap-3">
											<CheckCircle2 className="w-5 h-5 text-teal-400 mt-0.5 shrink-0" />
											<span className="text-slate-300 leading-relaxed">{r}</span>
										</li>
									))}
								</ul>
							</Block>
						)}

						{galleries.map((g) => (
							<Block key={g.title} title={g.title}>
								{g.items.length === 1 ? (
									<div className="relative rounded-xl overflow-hidden border border-teal-500/15 bg-[#08201d]">
										<MediaSlide item={g.items[0]} alt={`${company} — ${g.title} 1`} className="w-full h-72 object-cover" />
									</div>
								) : (
									<MediaCarousel items={g.items} title={company} />
								)}
							</Block>
						))}
					</div>

					{/* Sidebar */}
					<aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24 lg:self-start">
						{links.length > 0 && (
							<div className="rounded-2xl border border-teal-500/15 bg-[#08201d]/60 p-6">
								<div className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-4">Links</div>
								<div className="flex flex-col gap-2">
									{links.map((l) => (
										<a
											key={l.label}
											href={l.url}
											target="_blank"
											rel="noopener noreferrer"
											className="flex items-center gap-2 text-sm text-slate-300 hover:text-teal-300 transition-colors"
										>
											<ExternalLink className="w-4 h-4 shrink-0 text-teal-400" />
											{l.label}
										</a>
									))}
								</div>
							</div>
						)}

						<div className="rounded-2xl border border-teal-500/15 bg-[#08201d]/60 p-6">
							<div className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-4">Details</div>
							<div className="space-y-3 text-sm text-slate-300">
								<div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-teal-400 shrink-0" />{period}</div>
								<div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-teal-400 shrink-0" />{location}</div>
								<div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-teal-400 shrink-0" />{company}</div>
							</div>
						</div>

						<Button asChild variant="outline" className="w-full border-teal-500/30 text-slate-300 hover:border-teal-400 hover:text-teal-300">
							<Link to="/#experience">← All experiences</Link>
						</Button>
					</aside>
				</div>
			</div>

			<Footer />
		</div>
	);
};

export default ExperienceDetail;
